import { ApolloError, useApolloClient, useMutation, useQuery } from "@apollo/client";
import CallSplitIcon from "@mui/icons-material/CallSplit";
import SaveIcon from "@mui/icons-material/Save";
import { Box, Button, Divider, Grid, Stack, Typography } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers-pro";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ConfirmDialog, ResourceInfoDisplay } from "components/ConfirmDialog";
import { Dialog } from "components/Dialog";
import { FormSelect } from "components/Forms/FormSelect";
import { FormTextField } from "components/Forms/FormTextField";
import { getUtcDisplayDate } from "encamp-shared/src/utils/date";
import { gql } from "generated-graphql";
import {
  WasteClassification,
  WasteStream,
  WasteStreamRegistrationStatus,
} from "generated-graphql/graphql";
import { useShowWasteStateRegistration } from "hooks/useWasteStateRegistration";
import { DateTime } from "luxon";
import { useMemo, useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router";

import { ProvisionalWasteStreamChip } from "../../ProvisionalWasteStreamChip";
import { useWasteFacilityState } from "../../WasteFacilityContext";
import { MergedDestinationsDataGrid } from "./MergedDestinationsDataGrid";
import { MergedWasteStreamClassificationInput } from "./MergedWasteStreamClassificationInput";
import { MergedWasteStreamCommentInput } from "./MergedWasteStreamCommentInput";
import { MergedWasteStreamInfo } from "./MergedWasteStreamInfo";
import { MergedWasteStreamInternalNotesInput } from "./MergedWasteStreamInternalNotesInput";
import { MergedWasteStreamNameInput } from "./MergedWasteStreamNameInput";
import { MergedWasteStreamsDataGrid } from "./MergedWasteStreamsDataGrid";

type WasteStreamUnmergeFormData = {
  comment?: string | null;
  internalNote?: string | null;
  wasteStreamName: string;
  wasteStreamId: string;
  classification?: WasteClassification;
  stateRegisteredId: string;
  stateRegistrationStatus: WasteStreamRegistrationStatus | null;
  stateRegisteredAt: DateTime | null;
};

type WasteStreamUnmergeDialogProps = {
  onClose: () => void;
  wasteStream: Omit<WasteStream, "facility">;
  /** Facility state, used to gate the state-registration fields. */
  facilityState?: string;
};

const ORIGINAL_WASTE_STREAMS = gql(`
  query OriginalWasteStreams($wasteStreamId: ID!, $startDate: Date, $endDate: Date, $view: FacilityWasteView, $tags: [String!] ) {
    originalWasteStreamsById(wasteStreamId: $wasteStreamId, startDate: $startDate, endDate: $endDate, view: $view, tags: $tags) {
      items {
        id
        name
        vendorProfileNumber
        eManifestProfileNumber
        federalWasteCodes
        stateWasteCodes
        classification
        formCode
        sourceCode
        vendorAmount
        eManifestAmount
        vendorAmountAllTime
        eManifestAmountAllTime
      }
      count
    }
  }
`);

const UNMERGE_WASTE_STREAMS = gql(`
  mutation UnmergeWasteStreams($wasteStreamId: ID!) {
    unmergeWasteStreams(input:  {
       wasteStreamId: $wasteStreamId
    })
  }
`);

const EDIT_WASTE_STREAM = gql(`
  mutation EditWasteStream($id: ID!, $name: String, $classification: WasteClassification, $comment: String, $internalNote: String, $stateRegisteredId: String, $stateRegistrationStatus: WasteStreamRegistrationStatus, $stateRegisteredAt: DateTime) {
    updateWasteStream(input:  {
       id: $id
       name: $name
       classification: $classification
       comment: $comment
       internalNote: $internalNote
       stateRegisteredId: $stateRegisteredId
       stateRegistrationStatus: $stateRegistrationStatus
       stateRegisteredAt: $stateRegisteredAt
    })
  }
`);

export const WasteStreamUnmergeDialog = ({
  onClose,
  wasteStream,
  facilityState,
}: WasteStreamUnmergeDialogProps) => {
  const alerts = useAlerts();
  const navigate = useNavigate();
  const client = useApolloClient();
  const showStateRegistration = useShowWasteStateRegistration(facilityState);
  const {
    wasteFacilityState: { deferredFilters },
    effectiveWasteFacilityId,
  } = useWasteFacilityState();

  const defaultValues: WasteStreamUnmergeFormData = useMemo(
    () => ({
      comment: wasteStream.comment,
      internalNote: wasteStream.internalNote,
      wasteStreamName: wasteStream.name,
      wasteStreamId: wasteStream.id,
      classification: wasteStream.classification ?? undefined,
      stateRegisteredId: wasteStream.stateRegisteredId ?? "",
      stateRegistrationStatus: wasteStream.stateRegistrationStatus ?? null,
      stateRegisteredAt: wasteStream.stateRegisteredAt
        ? DateTime.fromISO(wasteStream.stateRegisteredAt)
        : null,
    }),
    [wasteStream]
  );

  const methods = useForm<WasteStreamUnmergeFormData>({
    defaultValues,
  });

  const { data, loading } = useQuery(ORIGINAL_WASTE_STREAMS, {
    variables: {
      wasteStreamId: wasteStream.id,
      startDate: deferredFilters.startDate,
      endDate: deferredFilters.endDate,
      view: deferredFilters.view,
      tags: deferredFilters.tags,
    },
  });

  const [unmerge, { loading: unmerging }] = useMutation(UNMERGE_WASTE_STREAMS, {
    variables: {
      wasteStreamId: wasteStream.id,
    },
  });

  // The edit mutation returns no fields, so Apollo can't auto-update the
  // normalized cache. Refetch the detail query (which drives this dialog's
  // values) and the grid list so saved edits — including the state
  // registration fields — show without a manual reload.
  const [edit, { loading: editing }] = useMutation(EDIT_WASTE_STREAM, {
    refetchQueries: ["GetWasteStream", "WasteStreams"],
  });

  const originalWasteStreams = useMemo(() => data?.originalWasteStreamsById?.items ?? [], [data]);

  const originalWasteStreamNames = useMemo(
    () => originalWasteStreams.map((ws) => ws.name),
    [originalWasteStreams]
  );

  const {
    control,
    handleSubmit,
    formState: { isDirty },
  } = methods;
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const handleSave = () => {
    handleSubmit(async (data) => {
      try {
        // toISO() returns null for an invalid DateTime; send null only when the
        // field was actually cleared, never as a side effect of a bad input.
        const stateRegisteredAt =
          data.stateRegisteredAt && data.stateRegisteredAt.isValid
            ? data.stateRegisteredAt.toISO()
            : null;
        await edit({
          variables: {
            id: data.wasteStreamId,
            name: data.wasteStreamName,
            classification: data.classification,
            comment: data.comment,
            internalNote: data.internalNote,
            stateRegisteredId: data.stateRegisteredId.trim() || null,
            stateRegistrationStatus: data.stateRegistrationStatus,
            stateRegisteredAt,
          },
        });
        alerts.success("Successfully edited waste stream");
      } catch (err) {
        alerts.error("An error occurred editing the waste stream", err);
        console.error(err);
      }
      onClose();
    })();
  };

  const handleUnmergeClick = () => {
    setShowConfirmDialog(true);
  };

  const handleCancelUnmerge = () => {
    setShowConfirmDialog(false);
  };

  const handleConfirmUnmerge = async () => {
    setShowConfirmDialog(false);
    try {
      await unmerge();
      alerts.success("Successfully unmerged");
      // Refetch only the WasteStreams query for the current facility.
      // See comment in WasteStreamMergeDialog for details on why we
      // use client.refetchQueries with onQueryUpdated.
      await client.refetchQueries({
        include: ["WasteStreams"],
        onQueryUpdated(observableQuery) {
          if (observableQuery.variables?.facilityId === effectiveWasteFacilityId) {
            return observableQuery.refetch();
          }
          return false;
        },
      });
    } catch (err) {
      console.error(err);
      const message =
        err instanceof ApolloError
          ? err.graphQLErrors.map((e) => e.message).join("; ") || err.message
          : err instanceof Error
            ? err.message
            : "An unexpected error occurred";
      alerts.error("There was an error unmerging this wastestream", message);
    }
    navigate(-1);
  };

  const saving = useMemo(() => unmerging || editing, [unmerging, editing]);

  const dateRangeText = useMemo(() => {
    const startDate =
      deferredFilters.startDate && getUtcDisplayDate(deferredFilters.startDate, "short");
    const endDate = deferredFilters.endDate && getUtcDisplayDate(deferredFilters.endDate, "short");
    if (!startDate || !endDate) return "";
    return `${startDate}-${endDate}`;
  }, [deferredFilters.startDate, deferredFilters.endDate]);

  return (
    <FormProvider {...methods}>
      <Dialog
        open={true}
        onClose={onClose}
        title="Edit merged waste stream"
        maxWidth="lg"
        fullWidth
        noPrimaryAction={true}
        secondaryActionElements={[
          <Button key="cancel" onClick={onClose}>
            Cancel
          </Button>,
        ]}
        additionalPrimaryActionElements={[
          <Button
            key="unmerge"
            variant="contained"
            onClick={handleUnmergeClick}
            disabled={saving}
            startIcon={<CallSplitIcon />}
          >
            Unmerge Waste Streams
          </Button>,
          <Button
            key="save"
            variant="contained"
            disabled={saving || !isDirty}
            onClick={handleSave}
            startIcon={<SaveIcon />}
            sx={{
              mr: 1,
            }}
          >
            Save
          </Button>,
        ]}
      >
        <Box>
          {wasteStream.isProvisional && (
            <Box sx={{ mb: 2 }}>
              <ProvisionalWasteStreamChip isProvisional />
            </Box>
          )}
          {/* Helper text */}
          <Typography sx={{ mb: 3, color: "text.secondary" }}>
            View original waste streams, merged data, destinations/management methods. Unmerge if
            needed.
          </Typography>

          {/* Original waste streams */}
          <Typography sx={{ mb: 3 }}>Original waste streams</Typography>
          <MergedWasteStreamsDataGrid
            wasteStreams={originalWasteStreams as WasteStream[]}
            dateRangeText={dateRangeText}
            loading={loading}
          />

          {/* Merged waste streams section */}
          <Typography sx={{ my: 3 }}>Merged waste streams</Typography>

          {/* Name and classification input */}
          <Grid container spacing={3}>
            <Grid size={6}>
              <MergedWasteStreamNameInput
                control={control}
                wasteStreamNames={originalWasteStreamNames}
              />
            </Grid>
            <Grid size={6}>
              <MergedWasteStreamClassificationInput control={control} />
            </Grid>
          </Grid>

          {/* State registration fields (regulated states only) */}
          {showStateRegistration && (
            <Grid container spacing={3} sx={{ mt: 3 }}>
              <Grid size={4}>
                <FormTextField
                  name="stateRegisteredId"
                  label="State Registered ID"
                  fullWidth
                  control={control}
                  helperText="Identifier assigned by the state"
                />
              </Grid>
              <Grid size={4}>
                <FormSelect
                  name="stateRegistrationStatus"
                  label="Registration Status"
                  hideErrors
                  selectItems={[
                    {
                      value: WasteStreamRegistrationStatus.NotRegistered,
                      display: "Not Registered",
                    },
                    { value: WasteStreamRegistrationStatus.InProgress, display: "In Progress" },
                    { value: WasteStreamRegistrationStatus.Registered, display: "Registered" },
                  ]}
                  control={control}
                />
              </Grid>
              <Grid size={4}>
                <Controller
                  name="stateRegisteredAt"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <DatePicker
                      label="Registered At"
                      value={value}
                      onChange={onChange}
                      slotProps={{ textField: { fullWidth: true } }}
                    />
                  )}
                />
              </Grid>
            </Grid>
          )}

          {/* Info section */}
          <Box sx={{ mt: 3 }}>
            <MergedWasteStreamInfo
              wasteStreams={[wasteStream]}
              dateRangeText={dateRangeText}
              isEditMode={true}
            />
          </Box>

          {/* Comment field */}
          <Box sx={{ mt: 3 }}>
            <MergedWasteStreamCommentInput control={control} />
          </Box>

          {/* Internal Note field */}
          <Box sx={{ mt: 3 }}>
            <MergedWasteStreamInternalNotesInput control={control} />
          </Box>

          {/* Merged Destinations Data Grid */}
          <MergedDestinationsDataGrid wasteStreamIds={wasteStream?.id ? [wasteStream.id] : []} />
        </Box>
      </Dialog>

      {/* Confirmation Dialog */}
      <ConfirmDialog
        header="Are you sure you want to unmerge these waste streams?"
        open={showConfirmDialog}
        onClose={handleCancelUnmerge}
        onCancel={handleCancelUnmerge}
        onConfirm={handleConfirmUnmerge}
        confirmText="Yes, Unmerge"
        confirmStartIcon={<CallSplitIcon />}
        loading={unmerging}
      >
        <Stack spacing={2}>
          {/* Merged Waste Stream */}
          <Box>
            <Grid container alignItems="center">
              <ResourceInfoDisplay
                resourceType="Merged Waste Stream"
                resourceName={wasteStream.name}
                labelSize={4}
                valueSize={8}
              />
              <ResourceInfoDisplay
                resourceType="Aggregated Amount"
                resourceName={wasteStream.vendorAmount || wasteStream.eManifestAmount}
                labelSize={4}
                valueSize={8}
              />
            </Grid>
          </Box>

          <Divider />

          {/* Original Waste Streams */}
          <Box>
            <Stack spacing={1}>
              {originalWasteStreams.map((ws) => (
                <Grid container key={ws.id} alignItems="center">
                  <ResourceInfoDisplay
                    resourceType="Original Waste Stream"
                    resourceName={ws.name}
                    labelSize={4}
                    valueSize={8}
                  />
                  <ResourceInfoDisplay
                    resourceType="Amount [All-time]"
                    resourceName={ws.vendorAmountAllTime || ws.eManifestAmountAllTime}
                    labelSize={4}
                    valueSize={8}
                  />
                </Grid>
              ))}
            </Stack>
          </Box>

          {/* Warning text */}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This action will separate these waste streams for all-time data and cannot be undone.
            Any changes made to the waste stream name will not be saved.
          </Typography>
        </Stack>
      </ConfirmDialog>
    </FormProvider>
  );
};
